import React from 'react';
import { observer } from 'mobx-react';
import { Button } from 'reactstrap';
import { getAppContext } from '~/lib/AppContext';
import { SiteServicesApiClient, SiteServicesEndpoint } from '~/lib/SiteServicesApiClient';
import { LoadStatus } from '~/lib/store/LoadingTracker';
import { SupportCodeStore, supportCodeClipboardText, supportCodeExplanation } from '~/lib/store/SupportCodeStore';
import CopyableText from '~/components/CopyableText';
import RedirectCard from '~/components/RedirectCard';

// The code is a convenience on the way to the portal, not a prerequisite for it, so a slow response
// forwards without it rather than holding the visitor on a spinner while WebApi is struggling.
export const supportCodeWaitMs = 5000;

/**
 * Forwards any unrecognised /support path to the public support portal, pausing first to show the
 * support code when the visitor has one. This is the last point at which the panel can put it in
 * front of someone who is on their way to raise a ticket.
 *
 * The pause is skipped for anyone without a code, so an anonymous visitor still lands on the portal.
 */
@observer
export default class PortalRedirect extends React.Component {
    // /support is exempt from the boot account load (PanelApp.skipsAccountLoad) so that an
    // unauthenticated visitor reaches the portal rather than the login page. This client is built
    // without the auth-required callback for the same reason: a 401 here has to reject quietly
    // instead of latching the login redirect and hijacking a public route.
    private store = new SupportCodeStore(
        new SiteServicesApiClient(SiteServicesEndpoint, getAppContext().config.csrfToken)
    );

    private slowCodeTimer?: ReturnType<typeof setTimeout>;

    componentDidMount() {
        this.store.ensureLoaded().catch(() => this.forwardToPortal());
        this.slowCodeTimer = setTimeout(() => {
            if (this.store.status === LoadStatus.Loading) this.forwardToPortal();
        }, supportCodeWaitMs);
    }

    componentWillUnmount() {
        clearTimeout(this.slowCodeTimer);
    }

    forwardToPortal() {
        const appContext = getAppContext();
        appContext.navigate(appContext.config.supportSite, true);
    }

    render() {
        const code = this.store.item;
        if (this.store.status !== LoadStatus.Complete || !code) {
            return <RedirectCard message="Redirecting to support..." />;
        }

        return (
            <RedirectCard title="Your Support Code" message={supportCodeExplanation}>
                <div className="mb-4">
                    <CopyableText text={supportCodeClipboardText(code)} contextText="support code">
                        <span className="font-monospace fs-5">{code}</span>
                    </CopyableText>
                </div>
                <Button color="primary" onClick={() => this.forwardToPortal()}>
                    Continue to the help desk
                </Button>
            </RedirectCard>
        );
    }
}
